import { useEffect, useRef, useState } from "react";
import { Facebook, Instagram, Youtube, Music2, Chrome, TrendingUp, Activity } from "lucide-react";

function useCountUp(target: number, start: boolean, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start, duration]);
  return val;
}

export function HeroBrainVisual() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.25 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const spend = useCountUp(24840, visible);
  const roas = useCountUp(4.2, visible);
  const leads = useCountUp(1847, visible);
  const campaigns = useCountUp(12, visible);

  // Chart paths (30 days)
  const spendPts = [20,28,24,32,38,30,42,48,40,52,46,58,54,62,68,60,72,78,70,82,76,86,80,88,84,92,86,94,90,96];
  const roasPts =  [30,34,32,38,42,40,48,46,52,50,58,54,60,62,68,64,70,72,68,74,72,78,74,80,76,82,78,84,80,86];
  const toPath = (pts: number[], h = 90) => {
    const step = 280 / (pts.length - 1);
    return pts.map((v, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - (v * (h / 100))}`).join(" ");
  };
  const toArea = (pts: number[], h = 90) => `${toPath(pts, h)} L 280 ${h} L 0 ${h} Z`;

  const platforms = [
    { Icon: Facebook,  color: "#1877F2", style: { top: "8%",  left: "-4%" },  delay: "0s" },
    { Icon: Instagram, color: "#E1306C", style: { top: "62%", left: "-6%" },  delay: "0.6s" },
    { Icon: Chrome,    color: "#4285F4", style: { top: "-6%", right: "12%" }, delay: "1.2s" },
    { Icon: Music2,    color: "#fff",    style: { top: "70%", right: "-5%" }, delay: "0.3s", bg: "#000" },
    { Icon: Youtube,   color: "#FF0000", style: { top: "18%", right: "-4%" }, delay: "0.9s" },
  ];

  return (
    <div
      ref={ref}
      className="relative mx-auto my-10 w-full max-w-5xl select-none [perspective:1600px]"
      aria-hidden="true"
    >
      <style>{`
        @keyframes heroBob {
          0%, 100% { transform: rotateY(-6deg) rotateX(4deg) translateY(-4px); }
          50%      { transform: rotateY(-6deg) rotateX(4deg) translateY(4px); }
        }
        @keyframes iconFloat {
          0%, 100% { transform: translateY(-8px); }
          50%      { transform: translateY(8px); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.25); }
        }
        @keyframes dashFlow {
          to { stroke-dashoffset: -20; }
        }
        .hero-bob { animation: heroBob 4s ease-in-out infinite; transform-style: preserve-3d; }
        .icon-float { animation: iconFloat 4.5s ease-in-out infinite; }
        .pulse-dot { animation: pulseDot 1.6s ease-in-out infinite; }
        .dash-flow { stroke-dasharray: 4 6; animation: dashFlow 1.5s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hero-bob, .icon-float, .pulse-dot, .dash-flow { animation: none; }
        }
      `}</style>

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 100 70" preserveAspectRatio="none">
        <line x1="2" y1="10"  x2="22" y2="22" stroke="#e8721a" strokeWidth="0.15" className="dash-flow" opacity="0.5" />
        <line x1="2" y1="48"  x2="22" y2="40" stroke="#e8721a" strokeWidth="0.15" className="dash-flow" opacity="0.5" />
        <line x1="98" y1="14" x2="78" y2="24" stroke="#6366f1" strokeWidth="0.15" className="dash-flow" opacity="0.5" />
        <line x1="98" y1="50" x2="78" y2="42" stroke="#6366f1" strokeWidth="0.15" className="dash-flow" opacity="0.5" />
        <line x1="80" y1="2"  x2="65" y2="18" stroke="#e8721a" strokeWidth="0.15" className="dash-flow" opacity="0.5" />
      </svg>

      {/* Floating platform icons */}
      {platforms.map(({ Icon, color, style, delay, bg }, i) => (
        <div
          key={i}
          className="absolute z-20 icon-float"
          style={{ ...style, animationDelay: delay }}
        >
          <div
            className="flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-2xl border border-white/10 shadow-2xl backdrop-blur"
            style={{ background: bg ?? "rgba(20,20,20,0.9)" }}
          >
            <Icon className="h-6 w-6 md:h-7 md:w-7" style={{ color }} />
          </div>
        </div>
      ))}

      {/* Browser window */}
      <div className="relative z-10 hero-bob">
        <div
          className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0a0a0a]"
          style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.7), 0 0 60px -10px rgba(232,114,26,0.15)" }}
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#1a1a1a] border-b border-white/10">
            <div className="flex gap-1.5">
              <span className="h-3 w-3 rounded-full bg-[#ff5f56]" />
              <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
              <span className="h-3 w-3 rounded-full bg-[#27c93f]" />
            </div>
            <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-[#2a2a2a] text-[10px] md:text-xs text-zinc-400 font-mono truncate">
              🔒 app.adkavi.ai/dashboard
            </div>
            <div className="hidden md:flex gap-1">
              <span className="h-1 w-1 rounded-full bg-zinc-600" />
              <span className="h-1 w-1 rounded-full bg-zinc-600" />
              <span className="h-1 w-1 rounded-full bg-zinc-600" />
            </div>
          </div>

          {/* Dashboard content */}
          <div className="p-3 md:p-5 bg-gradient-to-br from-[#0a0a0a] to-[#111]">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 md:h-7 md:w-7 rounded-md bg-gradient-to-br from-[#e8721a] to-[#f59e0b] flex items-center justify-center">
                  <span className="text-white font-bold text-xs">A</span>
                </div>
                <span className="text-white font-semibold text-sm md:text-base">Dashboard</span>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] md:text-xs text-zinc-300">
                  Last 30 days
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  <span className="text-[10px] md:text-xs text-emerald-400 font-medium">Live</span>
                </div>
              </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-4 gap-2 md:gap-3 mb-3 md:mb-4">
              {[
                { label: "Total Spend", value: `$${Math.round(spend).toLocaleString()}`, accent: "#e8721a" },
                { label: "ROAS",        value: `${roas.toFixed(1)}x`,                     accent: "#10b981" },
                { label: "Leads",       value: Math.round(leads).toLocaleString(),         accent: "#6366f1" },
                { label: "Campaigns",   value: `${Math.round(campaigns)} Active`,          accent: "#f59e0b" },
              ].map((k) => (
                <div key={k.label} className="rounded-lg bg-white/[0.03] border border-white/10 p-2 md:p-3">
                  <div className="text-[8px] md:text-[10px] uppercase tracking-wide text-zinc-500 mb-1">{k.label}</div>
                  <div className="text-xs md:text-lg font-semibold" style={{ color: k.accent }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2 md:p-3 mb-3 md:mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[10px] md:text-xs text-zinc-300">
                  <TrendingUp className="h-3 w-3 md:h-3.5 md:w-3.5 text-[#e8721a]" />
                  <span>Spend vs ROAS</span>
                </div>
                <div className="flex items-center gap-3 text-[8px] md:text-[10px]">
                  <span className="flex items-center gap-1 text-zinc-400"><span className="h-1.5 w-1.5 rounded-full bg-[#e8721a]" /> Spend</span>
                  <span className="flex items-center gap-1 text-zinc-400"><span className="h-1.5 w-1.5 rounded-full bg-[#6366f1]" /> ROAS</span>
                </div>
              </div>
              <svg viewBox="0 0 280 90" className="w-full h-16 md:h-24" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="spendGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#e8721a" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#e8721a" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="roasGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={toArea(spendPts)} fill="url(#spendGrad)" />
                <path d={toPath(spendPts)} fill="none" stroke="#e8721a" strokeWidth="1.5" />
                <path d={toArea(roasPts)} fill="url(#roasGrad)" />
                <path d={toPath(roasPts)} fill="none" stroke="#6366f1" strokeWidth="1.5" />
              </svg>
            </div>

            {/* Platform cards */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {[
                { name: "Meta",   spend: "$8.2k",  roas: "3.8x", color: "#1877F2" },
                { name: "TikTok", spend: "$6.1k",  roas: "4.9x", color: "#ff0050" },
                { name: "Google", spend: "$10.5k", roas: "4.1x", color: "#4285F4" },
              ].map((p) => (
                <div key={p.name} className="rounded-lg bg-white/[0.03] border border-white/10 p-2 md:p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] md:text-xs font-medium text-white">{p.name}</span>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <div className="text-[8px] md:text-[10px] text-zinc-500">Spend</div>
                      <div className="text-xs md:text-sm font-semibold text-white">{p.spend}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[8px] md:text-[10px] text-zinc-500">ROAS</div>
                      <div className="text-xs md:text-sm font-semibold text-emerald-400">{p.roas}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reflection/shadow */}
        <div
          className="absolute -bottom-8 left-[10%] right-[10%] h-12 rounded-full blur-2xl -z-10"
          style={{ background: "radial-gradient(ellipse, rgba(232,114,26,0.25), transparent 70%)" }}
        />
      </div>
    </div>
  );
}
